import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

const useStyles = makeStyles((theme) => ({
    stepBox: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1),
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.grey[50],
        borderRadius: "0 4px 4px 0",
    },
    stepHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(1),
    },
    stepLabel: {
        fontWeight: "bold",
        color: theme.palette.text.secondary,
    },
    stepResponse: {
        fontSize: "1.1rem",
        fontWeight: 500,
    },
    summaryBox: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        borderRadius: 8,
        marginBottom: theme.spacing(3),
    },
    mediaImage: {
        maxWidth: "100%",
        maxHeight: 250,
        borderRadius: 8,
        border: "1px solid #ddd",
        cursor: "pointer",
        marginTop: theme.spacing(1),
        transition: "transform 0.2s",
        "&:hover": {
            transform: "scale(1.02)"
        }
    }
}));

const OrderModal = ({ open, onClose, order }) => {
    const classes = useStyles();

    if (!order) return null;

    const flowData = order.flowData || { steps: [], summary: {} };

    const getStatusColor = (status) => {
        switch (status) {
            case "completed": return "primary";
            case "active": return "secondary";
            case "cancelled": return "default";
            default: return "default";
        }
    };

    const renderStepContent = (step) => {
        if (step.nodeType === "carousel") {
            return (
                <>
                    <Typography variant="caption" display="block">Card: {step.cardTitle}</Typography>
                    {step.buttonClicked}
                </>
            );
        }

        if (step.nodeType === "button") {
            return step.buttonClicked;
        }

        const response = step.userResponse;
        if (!response) return "Não informado";

        const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8080/";

        // Check if response is a local filename (image) or ends with media extensions
        const isLocalImage = (typeof response === "string") && (
            response.toLowerCase().endsWith(".jpg") ||
            response.toLowerCase().endsWith(".jpeg") ||
            response.toLowerCase().endsWith(".png") ||
            response.toLowerCase().endsWith(".webp")
        );

        if (isLocalImage) {
            const fullUrl = `${backendUrl}public/${response}`;
            return (
                <Box mt={1}>
                    <Typography variant="caption" display="block" gutterBottom>
                        📸 Comprovante/Imagem (Alta Definição):
                    </Typography>
                    <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                        <img
                            src={fullUrl}
                            alt="Comprovante"
                            className={classes.mediaImage}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/150?text=Erro+ao+carregar";
                            }}
                        />
                    </a>
                </Box>
            );
        }

        // Fallback for legacy JSON media object (remains for backward compatibility)
        if (typeof response === "string" && response.trim().startsWith("{") && response.includes("mimetype")) {
            try {
                const mediaData = JSON.parse(response);
                if (mediaData.mimetype?.startsWith("image/")) {
                    return (
                        <Box mt={1}>
                            <Typography variant="caption" display="block" gutterBottom>
                                🖼️ Comprovante (WhatsApp Link):
                            </Typography>
                            <a href={mediaData.URL} target="_blank" rel="noopener noreferrer">
                                <img
                                    src={mediaData.JPEGThumbnail ? `data:image/jpeg;base64,${mediaData.JPEGThumbnail}` : mediaData.URL}
                                    alt="Comprovante"
                                    className={classes.mediaImage}
                                />
                            </a>
                        </Box>
                    );
                }
            } catch (e) { }
        }

        return response;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Detalhes do Pedido #{order.id}</Typography>
                    <Chip
                        size="small"
                        label={order.status.toUpperCase()}
                        color={getStatusColor(order.status)}
                    />
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Box className={classes.summaryBox}>
                    <Typography variant="subtitle2">Cliente</Typography>
                    <Typography variant="h6">{order.contactName}</Typography>
                    <Typography variant="body2">{order.contactNumber}</Typography>
                    <Box mt={1}>
                        <Typography variant="subtitle2">Endereço de Entrega</Typography>
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                            {(() => {
                                const flowData = typeof order.flowData === 'string' ? JSON.parse(order.flowData) : order.flowData;
                                const addressStep = flowData?.steps?.find(s => s.nodeType === "order_address");
                                return addressStep ? addressStep.userResponse : "Não informado";
                            })()}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={2}>
                        <Box>
                            <Typography variant="subtitle2">Início</Typography>
                            <Typography variant="body2">
                                {format(parseISO(order.startedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                            </Typography>
                        </Box>
                        <Box textAlign="right">
                            <Typography variant="subtitle2">Valor Total</Typography>
                            <Typography variant="h5">
                                R$ {parseFloat(order.totalValue).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Typography variant="h6" gutterBottom>Fluxo do Pedido</Typography>
                {flowData.steps && flowData.steps.length > 0 ? (
                    flowData.steps.map((step, index) => (
                        <Box key={index} className={classes.stepBox}>
                            <Box className={classes.stepHeader}>
                                <Typography className={classes.stepLabel}>
                                    {step.stepNumber}. {step.nodeLabel}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {format(parseISO(step.timestamp), "HH:mm:ss")}
                                </Typography>
                            </Box>

                            <Typography component="div" className={classes.stepResponse}>
                                {renderStepContent(step)}
                            </Typography>

                            {step.value && step.nodeType !== "price" && (
                                <Typography variant="body2" color="primary" style={{ marginTop: 4, fontWeight: "bold" }}>
                                    Valor: R$ {step.value.toFixed(2)}
                                </Typography>
                            )}
                        </Box>
                    ))
                ) : (
                    <Typography color="textSecondary">Nenhuma interação registrada ainda.</Typography>
                )}

                {order.notes && (
                    <Box mt={3}>
                        <Typography variant="h6" gutterBottom>Notas</Typography>
                        <Typography variant="body2">{order.notes}</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderModal;
